import React, { useState } from 'react';
import { Contact, RecoverAccountOptions, useResetPasswordInitMutation } from '../../../slices/auth-api.slice';
import { useSelector } from 'react-redux';
import { selectUserEmail } from '../../../slices/login.slice';
import { PrimaryButton } from '@workday/canvas-kit-react/button';
import { autoId } from '@workday/upc-common';
import { ID_RESET_PASSWORD_EMAIL_INIT, ID_RESET_PASSWORD_UNABLE_TO_ACCESS_EMAIL } from '../../../autoIds';
import styles from './send-email-otp-step.module.scss';
import { maskEmail } from '../../../utils/string';
import { Flex } from '@workday/canvas-kit-react';
import { TertiaryButtonLink } from '../tertiary-button-link';
import { ERROR_PATH } from '../../../react-router-paths';
import { useTranslation } from 'react-i18next';
import { AlternativeContactLink } from '../alternative-contact-link';
import { phoneContactLinkProps } from './phoneContactLinkProps';
import { useHistory } from 'react-router-dom';
import { UNAUTH_FLOW } from '../../../shared/error-page/error-page';
import { isFetchBaseQueryError } from '../../../utils/server-error';
import { LoginLink } from '../../../shared/login-link/login-link';

interface SendEmailOtpStepProps extends RecoverAccountOptions {
    contacts: Array<Contact>;
    onNext: () => void;
}

export function SendEmailOtpStep({ onNext, contacts, recoverAccount }: SendEmailOtpStepProps) {
    const history = useHistory();

    const [tooManyOtpAttempts, setTooManyOtpAttempts] = useState(false);

    const userEmail = useSelector(selectUserEmail);
    const [resetPasswordSendEmailOtp] = useResetPasswordInitMutation();

    const { t } = useTranslation('auth');

    async function onResetPasswordClick() {
        try {
            await resetPasswordSendEmailOtp({ email: userEmail, recoverAccount }).unwrap();
            onNext();
        } catch (e) {
            if (isFetchBaseQueryError(e) && e.status === 429) {
                setTooManyOtpAttempts(true);
            } else {
                history.replace({
                    pathname: ERROR_PATH,
                    state: {
                        context: UNAUTH_FLOW
                    }
                });
            }
        }
    }

    const hasPhoneContact = contacts.some(contact => contact.contactType === 'phone');

    return (
        <div className={styles.resetPasswordEmailEntry}>
            {tooManyOtpAttempts ? (
                <>
                    <h1 className={styles.title}>{t('SendPhoneOtpStep.tooManyPasscodeAttemptsTitle')}</h1>
                    <p className={styles.body}>{t('SendPhoneOtpStep.tooManyPasscodeAttemptsText')}</p>
                </>
            ) : (
                <>
                    <h1 className={styles.title}>{t('SendEmailOtpStep.title')}</h1>
                    <p className={styles.body}>
                        {recoverAccount ? t('SendEmailOtpStep.bodyRecoverAccount') : t('SendEmailOtpStep.body')}
                    </p>
                    <p className={styles.email}>{maskEmail(userEmail)}</p>
                    <PrimaryButton
                        type='button'
                        onClick={onResetPasswordClick}
                        {...autoId(ID_RESET_PASSWORD_EMAIL_INIT)}
                        size='large'
                        grow={true}
                        marginBottom='1rem'>
                        {t('SendEmailOtpStep.submitButton')}
                    </PrimaryButton>
                </>
            )}

            <Flex flexDirection='column'>
                {hasPhoneContact && <AlternativeContactLink {...phoneContactLinkProps} />}
                <TertiaryButtonLink
                    to={ERROR_PATH}
                    context={UNAUTH_FLOW}
                    {...autoId(ID_RESET_PASSWORD_UNABLE_TO_ACCESS_EMAIL)}>
                    {t('SendEmailOtpStep.unableToAccessEmail')}
                </TertiaryButtonLink>
                <LoginLink />
            </Flex>
        </div>
    );
}
